import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';

interface DraftModificationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (method: 'append' | 'insert', lineNumber?: number) => Promise<void>;
  currentContent: string;
  newContent: string;
  draftTitle: string;
  initialLineNumber?: number;
}

export const DraftModificationDialog: React.FC<DraftModificationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  currentContent,
  newContent,
  draftTitle,
  initialLineNumber,
}) => {
  const [method, setMethod] = useState<'append' | 'insert'>('append');
  const [lineNumber, setLineNumber] = useState(1);
  const [lineNumberInput, setLineNumberInput] = useState('1');
  const [isConfirming, setIsConfirming] = useState(false);

  // When dialog opens with a selected line, pre-fill the insert mode and line number
  useEffect(() => {
    if (isOpen && initialLineNumber !== undefined && initialLineNumber > 0) {
      setMethod('insert');
      setLineNumber(initialLineNumber);
      setLineNumberInput(initialLineNumber.toString());
    } else if (isOpen) {
      // Reset to defaults when opening without a selected line
      setMethod('append');
      setLineNumber(1);
      setLineNumberInput('1');
    }
  }, [isOpen, initialLineNumber]);

  const totalLines = useMemo(() => {
    return currentContent.split('\n').length;
  }, [currentContent]);

  // Calculate statistics
  const stats = useMemo(() => {
    const charCount = newContent.length;
    const wordCount = newContent.trim().split(/\s+/).filter(Boolean).length;
    return { charCount, wordCount };
  }, [newContent]);

  // Generate preview of modified content
  const previewContent = useMemo(() => {
    const lines = currentContent.split('\n');
    const newLines = newContent.split('\n');

    if (method === 'append') {
      // Show last few lines of current content + separator + new content
      const lastLines = lines.slice(-3);
      return [
        ...lastLines,
        '',
        '--- New content will be added below ---',
        '',
        ...newLines.slice(0, 5),
        newLines.length > 5 ? `... (${newLines.length - 5} more lines)` : '',
      ].filter(Boolean).join('\n');
    } else {
      // Insert preview
      const validLineNum = Math.max(1, Math.min(lineNumber, totalLines + 1));
      const beforeLines = lines.slice(Math.max(0, validLineNum - 3), validLineNum - 1);
      const afterLines = lines.slice(validLineNum - 1, validLineNum + 2);

      return [
        ...beforeLines,
        '',
        `--- New content will be inserted at line ${validLineNum} ---`,
        '',
        ...newLines.slice(0, 3),
        newLines.length > 3 ? `... (${newLines.length - 3} more lines)` : '',
        '',
        '--- Existing content continues ---',
        '',
        ...afterLines,
      ].filter(Boolean).join('\n');
    }
  }, [currentContent, newContent, method, lineNumber, totalLines]);

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      // Validate line number for insert method
      const validLineNumber = method === 'insert'
        ? Math.max(1, Math.min(lineNumber, totalLines + 1))
        : undefined;

      await onConfirm(method, validLineNumber);
      // Dialog will be closed by parent component after successful confirmation
    } catch (error) {
      console.error('Error confirming draft modification:', error);
      // Keep dialog open on error (parent component handles error notification)
    } finally {
      setIsConfirming(false);
    }
  };

  const handleLineNumberChange = (value: string) => {
    // Always update the input string (allows clearing)
    setLineNumberInput(value);

    // Parse and update the number if valid
    if (value === '') {
      setLineNumber(1); // Default for empty
    } else {
      const num = parseInt(value, 10);
      if (!isNaN(num) && num >= 0) {
        setLineNumber(num);
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Modify Draft: {draftTitle}</DialogTitle>
          <DialogDescription>
            Choose how to add the new content to your draft.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto flex flex-col gap-4 px-1">
          {/* Content Preview Tabs */}
          <Tabs defaultValue="preview" className="flex flex-col">
            <TabsList>
              <TabsTrigger value="preview">Preview Changes</TabsTrigger>
              <TabsTrigger value="new-content">New Content</TabsTrigger>
            </TabsList>

            <TabsContent value="preview" className="mt-2">
              <ScrollArea className="h-[200px] w-full rounded-md border p-4">
                <pre className="text-sm font-mono whitespace-pre-wrap">{previewContent}</pre>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="new-content" className="mt-2">
              <ScrollArea className="h-[200px] w-full rounded-md border p-4">
                <pre className="text-sm font-mono whitespace-pre-wrap">{newContent}</pre>
              </ScrollArea>
            </TabsContent>
          </Tabs>

          <Separator />

          {/* Method Selector */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Modification Method</Label>
            <RadioGroup value={method} onValueChange={(value) => setMethod(value as 'append' | 'insert')}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="append" id="append" />
                <Label htmlFor="append" className="font-normal cursor-pointer">
                  Append to end
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="insert" id="insert" />
                <Label htmlFor="insert" className="font-normal cursor-pointer">
                  Insert at line
                </Label>
              </div>
            </RadioGroup>

            {/* Line Number Input (only shown for insert method) */}
            {method === 'insert' && (
              <div className="ml-6 flex items-center gap-3">
                <Label htmlFor="lineNumber" className="whitespace-nowrap">
                  Line number:
                </Label>
                <Input
                  id="lineNumber"
                  type="number"
                  min={1}
                  max={totalLines + 1}
                  value={lineNumberInput}
                  onChange={(e) => handleLineNumberChange(e.target.value)}
                  onFocus={(e) => e.target.select()}
                  className="w-24"
                  autoComplete="off"
                />
                <span className="text-sm text-gray-500">
                  (1 to {totalLines + 1})
                </span>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex-row items-center justify-between sm:justify-between">
          {/* Statistics */}
          <div className="text-sm text-gray-600">
            +{stats.charCount} characters, +{stats.wordCount} words
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isConfirming}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleConfirm}
              disabled={isConfirming}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {isConfirming ? 'Confirming...' : 'Confirm'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
